﻿/**
 * Adapted from https://developer.mozilla.org/en-US/docs/Web/API/Document/cookie/Simple_document.cookie_framework
 */
export class Cookies {
  static getValue(name) {
    if (!name) {
      return null;
    }
    const encodedKey = encodeURIComponent(name);
    const replacedEncodedKey = encodedKey.replace(/[-.+*]/g, '\\$&');
    const regex = new RegExp('(?:(?:^|.*;)\\s*' + replacedEncodedKey + '\\s*\\=\\s*([^;]*).*$)|^.*$');
    return decodeURIComponent(document.cookie.replace(regex, '$1')) || null;
  }

  /**
   * Sets a cookie with all parameters.
   * @param {string} name
   * @param {string} value
   * @param {(number|string|Date)} [expiresOrMaxAge] - Can either be a number (including Infinity) representing a max-age,
   * or a string or Date representing the cookie's expiration date.
   * @param {string} [path]
   * @param {string} [domain]
   * @param {boolean} [secure]
   * @param {string} [sameSite]
   * @returns {boolean} True if a value was set, otherwise false.
   */
  static setValue(name, value, expiresOrMaxAge, path, domain, secure, sameSite) {
    if (!name || /^(?:expires|max-age|path|domain|secure)$/i.test(name)) {
      return false;
    }
    let expires = '';
    if (expiresOrMaxAge) {
      switch (expiresOrMaxAge.constructor) {
        case Number:
          expires = expiresOrMaxAge === Infinity
            ? '; expires=Fri, 31 Dec 9999 23:59:59 GMT'
            : '; max-age=' + expiresOrMaxAge;
          /*
          Note: Despite officially defined in RFC 6265, the use of `max-age` is not compatible with any
          version of Internet Explorer, Edge and some mobile browsers. Therefore passing a number to
          the end parameter might not work as expected. A possible solution might be to convert the the
          relative time to an absolute time. For instance, replacing the previous line with:
          */
          /*
          sExpires = vEnd === Infinity ? '; expires=Fri, 31 Dec 9999 23:59:59 GMT' : '; expires=' + (new Date(vEnd * 1e3 + Date.now())).toUTCString();
          */
          break;
        case String:
          expires = '; expires=' + expiresOrMaxAge;
          break;
        case Date:
          expires = '; expires=' + expiresOrMaxAge.toUTCString();
          break;
      }
    }
    document.cookie = encodeURIComponent(name) + '=' + encodeURIComponent(value) +
      expires +
      (domain ? '; domain=' + domain : '') +
      (path ? '; path=' + path : '') +
      (sameSite ? '; samesite=' + sameSite : '') +
      (secure ? '; secure' : '');
    return true;
  }

  /**
   * Removes the cookie.
   * @param {string} name
   * @param {string} [path]
   * @param {string} [domain]
   */
  static removeValue(name, path, domain) {
    if (!this.exists(name)) {
      return false;
    }
    document.cookie = encodeURIComponent(name) + '=; expires=Thu, 01 Jan 1970 00:00:00 GMT' +
      (domain ? '; domain=' + domain : '') + (path ? '; path=' + path : '');
    return true;
  }

  /**
   * Returns true if the cookie exists.
   */
  static exists(name) {
    if (!name || /^(?:expires|max-age|path|domain|secure)$/i.test(name)) {
      return false;
    }
    const pattern = '(?:^|;\\s*)' + encodeURIComponent(name).replace(/[-.+*]/g, '\\$&') + '\\s*\\=';
    return (new RegExp(pattern)).test(document.cookie);
  }

  /** Returns an array of all readable cookies at this location. */
  static keys() {
    const keysStr = document.cookie.replace(/((?:^|\s*;)[^=]+)(?=;|$)|^\s*|\s*(?:=[^;]*)?(?:\1|$)/g, '');
    const keys = keysStr.split(/\s*(?:=[^;]*)?;\s*/);
    for (var len = keys.length, i = 0; i < len; i++) {
      keys[i] = decodeURIComponent(keys[i]);
    }
    return keys;
  }
}